export type DraftSaveState = 'idle' | 'pending' | 'saving' | 'invalid' | 'failed';

/** Keeps the newest draft until its exact revision is confirmed. */
export function createConfirmedDraftAutosave<Draft>(options: {
  validate: (draft: Draft) => boolean;
  persist: (draft: Draft) => Promise<boolean>;
  onState: (state: DraftSaveState) => void;
  debounceMs?: number;
}) {
  let pending: {revision: number; draft: Draft} | null = null;
  let revision = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running: Promise<boolean> | null = null;
  let active = true;

  function stopTimer() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  async function drain(): Promise<boolean> {
    while (pending) {
      if (!active) return false;
      const candidate = pending;
      if (!options.validate(candidate.draft)) {
        stopTimer();
        options.onState('invalid');
        return false;
      }
      options.onState('saving');
      let confirmed = false;
      try {
        confirmed = await options.persist(candidate.draft);
      } catch {
        // An unexpected transport exception has the same retryable posture.
      }
      if (!active) return false;
      if (!confirmed) {
        stopTimer();
        options.onState('failed');
        return false;
      }
      if (pending.revision === candidate.revision) pending = null;
      // A newer edit must also validate and persist before navigation is safe.
    }
    options.onState('idle');
    return true;
  }

  function flush(): Promise<boolean> {
    stopTimer();
    if (!active) return Promise.resolve(false);
    if (running) return running;
    if (!pending) return Promise.resolve(true);
    running = drain().finally(() => {
      running = null;
    });
    return running;
  }

  return {
    update(draft: Draft) {
      if (!active) return;
      pending = {revision: ++revision, draft};
      options.onState('pending');
      stopTimer();
      timer = setTimeout(() => void flush(), options.debounceMs ?? 500);
    },
    flush,
    isDirty: () => pending !== null,
    // Forced auth teardown cannot cancel an existing transport attempt, but
    // must not start another revision or report its completion in a new session.
    suspend() {
      active = false;
      stopTimer();
    },
    resume() {
      active = true;
    },
  };
}
